import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Heart, Target, Eye, UserCircle2 } from "lucide-react";
import { Logo, Btn } from "@/components/kit";

const Section = ({ icon: Icon, eyebrow, title, children, delay = 0 }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="relative rounded-2xl bg-[#180735] border border-[#2E1256] p-8 lg:p-10 shadow-xl">
    <div className="flex items-center gap-3 mb-5">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF5E36] to-[#FF2A6D] flex items-center justify-center shadow-lg">
        <Icon size={20} className="text-white" />
      </div>
      <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#FF6B7D]">{eyebrow}</span>
    </div>
    <h2 className="font-head text-2xl sm:text-3xl font-extrabold text-white mb-5">{title}</h2>
    <div className="space-y-4 text-base text-purple-100/85 leading-relaxed max-w-3xl">{children}</div>
  </motion.section>
);

export default function About() {
  const navigate = useNavigate();
  return (
    <div className="bd-page" data-testid="about-page">
      <header className="sticky top-0 z-30 border-b border-[#2E1256] bg-[#0B031E]/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" data-testid="about-logo"><Logo /></Link>
          <button onClick={() => navigate("/")} data-testid="about-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-200 hover:text-white transition-colors">
            <ArrowLeft size={16} /> Back to home
          </button>
        </div>
      </header>

      <section className="max-w-4xl mx-auto px-6 pt-16 pb-10 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#451B7F] bg-[#180735]/60 text-xs font-semibold text-purple-200 mb-6">
          About BeetDrop
        </div>
        <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-tight">
          Why BeetDrop <span className="bd-grad-text">exists</span>.
        </h1>
        <div className="mt-6 space-y-4 text-base text-purple-100/85 leading-relaxed text-left sm:text-center max-w-3xl mx-auto">
          <p>BeetDrop was created from firsthand hospitality experience and an understanding of what happens when operational communication breaks down.</p>
          <p>Missed guest requests, inconsistent handovers, disconnected departments and unresolved complaints do not exist as isolated problems — they affect the entire guest experience.</p>
          <p>BeetDrop brings reservations, guest communication and operational workflows into one shared environment so teams can work with greater visibility and accountability.</p>
          <p>The platform is deliberately being built around practical hospitality workflows rather than unnecessary complexity.</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 pb-20 space-y-6">
        <Section icon={Target} eyebrow="Our Mission" title="Operate smarter. Communicate better.">
          <p>To help hospitality businesses operate smarter, communicate better and resolve guest issues before they become negative experiences or reviews.</p>
          <p>BeetDrop exists to improve communication, accountability and operational visibility while giving hospitality teams practical technology that supports the way they work.</p>
        </Section>

        <Section icon={Eye} eyebrow="Our Vision" title="A connected hospitality ecosystem." delay={0.05}>
          <p>To become the operating platform hospitality businesses trust to coordinate teams, improve guest satisfaction and elevate service standards worldwide.</p>
          <p>Our long-term vision is a connected hospitality ecosystem in which technology makes operations clearer, communication easier and service more responsive.</p>
          <p className="text-white font-semibold">Technology should support hospitality — not complicate it.</p>
        </Section>

        <Section icon={UserCircle2} eyebrow="Founder" title="Ragheema Abrahams — Founder" delay={0.1}>
          <p>BeetDrop was founded by Ragheema Abrahams, a hospitality professional with hands-on experience across hotel and food-service operations.</p>
          <p>Working within hospitality exposed recurring operational challenges including communication gaps between departments, missed guest information, inconsistent handovers and issues that could have been resolved before becoming poor guest experiences.</p>
          <p>That experience shaped BeetDrop from the beginning.</p>
          <p>Ragheema continues to guide the platform from an operational perspective, ensuring its development remains focused on practical hospitality needs, intuitive workflows and problems that genuinely exist within the industry.</p>
        </Section>

        <div className="rounded-2xl bg-gradient-to-br from-[#FF5E36]/15 to-[#FF2A6D]/10 border border-[#FF2A6D]/30 p-8 text-center">
          <div className="flex justify-center mb-4"><Heart size={28} className="text-[#FF6B7D]" /></div>
          <h3 className="font-head text-2xl font-extrabold text-white">Better Operations. Happier Guests. Stronger Businesses.</h3>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <Btn data-testid="about-cta-trial" onClick={() => navigate("/business/register")}>Start free trial</Btn>
            <Btn variant="secondary" data-testid="about-cta-signin" onClick={() => navigate("/login")}>Sign in</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
